import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createBoard, placeMark, checkWinner, isBoardFull } from "./engine.js";
import { LEVEL_CONFIGS } from "./types.js";
import type { TicTacToeGame } from "./types.js";
import ConfettiSplash from "../../lib/ConfettiSplash.js";

type Mode = "cpu" | "local" | "multiplayer";
type Level = "easy" | "medium" | "hard";
type PlayerColor = "red" | "yellow" | "green" | "purple" | "orange" | "teal";

const COLOR_OPTIONS: PlayerColor[] = ["red", "yellow", "green", "purple", "orange", "teal"];

const CODE_WORDS = [
  "apple", "beach", "candy", "dance", "eagle", "flame", "grape", "honey",
  "ivory", "jelly", "koala", "lemon", "mango", "noble", "ocean", "pearl",
  "queen", "rain", "sugar", "tiger", "umbra", "vivid", "whale", "xenon",
  "yacht", "zebra", "bloom", "coral", "dawn", "ember", "frost", "glow",
  "haven", "iris", "jade", "kite", "lune", "mist", "nova", "opal",
];

function generateCode(): string {
  const pick = () => CODE_WORDS[Math.floor(Math.random() * CODE_WORDS.length)];
  return `${pick()}-${pick()}-${pick()}`;
}

function toLabel(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export default function TicTacToePage({ hideTitle = false }: { hideTitle?: boolean }) {
  const [mode, setMode] = useState<Mode>("cpu");
  const [level, setLevel] = useState<Level>("easy");
  const [p1Name, setP1Name] = useState("Player 1");
  const [p2Name, setP2Name] = useState("Player 2");
  const [p1Color, setP1Color] = useState<PlayerColor>("red");
  const [p2Color, setP2Color] = useState<PlayerColor>("yellow");
  const [game, setGame] = useState<TicTacToeGame>(() => buildGame("easy"));
  const [message, setMessage] = useState("");

  // multiplayer state
  const [mpConnected, setMpConnected] = useState(false);
  const [mpPlayerId, setMpPlayerId] = useState<1 | 2 | null>(null);
  const [roomCode, setRoomCode] = useState("");
  const [mpToken, setMpToken] = useState("");
  const [opponentName, setOpponentName] = useState("");
  const [yourTurn, setYourTurn] = useState(false);
  const [mpError, setMpError] = useState("");
  const [joinCode, setJoinCode] = useState(generateCode);

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingRef = useRef(false);
  const roomCodeRef = useRef(roomCode);
  const mpPlayerIdRef = useRef(mpPlayerId);
  const mpConnectedRef = useRef(mpConnected);

  roomCodeRef.current = roomCode;
  mpPlayerIdRef.current = mpPlayerId;
  mpConnectedRef.current = mpConnected;

  const cfg = LEVEL_CONFIGS[level];
  const isCpuTurn = mode === "cpu" && game.turn === 2 && game.winner === 0 && !game.isDraw;

  const p1 = p1Name.trim() || "Player 1";
  const p2 = mode === "cpu" ? "Computer" : p2Name.trim() || "Player 2";

  const status = useMemo(() => {
    if (mode === "multiplayer") {
      if (game.winner === mpPlayerId) return "You win!";
      if (game.winner !== 0) return `${opponentName || "Opponent"} wins!`;
      if (game.isDraw) return "Draw game.";
      return yourTurn ? "Your turn" : `${opponentName || "Opponent"}'s turn`;
    }
    if (game.winner === 1) return `${p1} wins!`;
    if (game.winner === 2) return `${p2} wins!`;
    if (game.isDraw) return "Draw game.";
    return `${game.turn === 1 ? p1 : p2}'s turn`;
  }, [game, p1, p2, mode, mpPlayerId, yourTurn, opponentName]);

  // ---- API helpers --------------------------------------------------------

  async function apiPost(path: string, body: object) {
    try {
      const res = await fetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      return await res.json();
    } catch {
      return { type: "error", message: "Network error." };
    }
  }

  // ---- server event handling ----------------------------------------------

  function handleServerEvent(event: any) {
    switch (event.type) {
      case "opponent-joined":
        setOpponentName(event.playerName);
        setP2Color(event.playerColor);
        setMpConnected(true);
        setMessage(`Opponent ${event.playerName} joined! Press New Game to start.`);
        break;
      case "game-state":
        setGame(event.game);
        setYourTurn(event.yourTurn);
        break;
      case "opponent-left":
        setMpConnected(false);
        setRoomCode("");
        setOpponentName("");
        setMpPlayerId(null);
        setMpToken("");
        setMessage("Opponent left the game.");
        setGame(buildGame(level));
        break;
      case "error":
        setMpError(event.message);
        break;
    }
  }

  // ---- polling ------------------------------------------------------------

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = true;

    async function poll() {
      if (!pollingRef.current) return;
      const code = roomCodeRef.current;
      const pid = mpPlayerIdRef.current;
      if (!code || !pid) { pollingRef.current = false; return; }

      try {
        const res = await fetch(`/api/poll?code=${encodeURIComponent(code)}&playerId=${pid}`);
        const data = await res.json();
        for (const event of data.events) {
          handleServerEvent(event);
          if (event.type === "opponent-left") {
            pollingRef.current = false;
            return;
          }
        }
      } catch { /* retry */ }

      if (pollingRef.current) pollRef.current = setTimeout(poll, 200);
    }

    poll();
  }, []);

  function stopPolling() {
    pollingRef.current = false;
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
  }

  useEffect(() => { return () => stopPolling(); }, []);

  // ---- game logic ---------------------------------------------------------

  function buildGame(lvl: Level): TicTacToeGame {
    const c = LEVEL_CONFIGS[lvl];
    return {
      board: createBoard(c.size),
      size: c.size,
      connect: c.connect,
      turn: 1,
      winner: 0,
      winningCells: [],
      isDraw: false,
    };
  }

  function applyMove(r: number, c: number) {
    setGame((prev) => {
      if (prev.winner !== 0 || prev.isDraw) return prev;
      const result = placeMark(prev.board, r, c, prev.turn);
      if (!result.ok) return prev;
      const win = checkWinner(result.board, prev.connect);
      return {
        ...prev,
        board: result.board,
        winner: win.winner,
        winningCells: win.cells,
        isDraw: win.winner === 0 && isBoardFull(result.board),
        turn: prev.turn === 1 ? 2 : 1,
      };
    });
  }

  function handleClick(r: number, c: number) {
    if (mode === "multiplayer") {
      if (!yourTurn) return;
      apiPost("/api/ttt/move", { code: roomCode, playerId: mpPlayerId, token: mpToken, r, c });
      return;
    }
    if (isCpuTurn) return;
    applyMove(r, c);
  }

  function startNewGame() {
    if (mode === "multiplayer") {
      apiPost("/api/ttt/reset", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      return;
    }
    if (p1Color === p2Color) { setMessage("Players must use different colors."); return; }
    setGame(buildGame(level));
    setMessage("");
  }

  function handleModeChange(next: Mode) {
    if (mode === "multiplayer") {
      if (roomCode && mpToken) apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      stopPolling();
    }
    setMode(next);
    setGame(buildGame(level));
    if (next !== "multiplayer") setMessage("");
  }

  // ---- multiplayer room actions -------------------------------------------

  async function handleCreateRoom() {
    const result = await apiPost("/api/create-room", {
      playerName: p1,
      playerColor: p1Color,
      gameType: "tic-tac-toe",
    });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = result.code;
    mpPlayerIdRef.current = result.playerId;
    setRoomCode(result.code);
    setMpPlayerId(result.playerId);
    setMpToken(result.token);
    setMessage(`Room created! Share this code: ${result.code}`);
    startPolling();
  }

  async function handleJoinRoom() {
    if (!joinCode.trim()) { setMpError("Enter a room code."); return; }
    const result = await apiPost("/api/join-room", {
      code: joinCode.trim(),
      playerName: p1Name.trim() || "Player 2",
      playerColor: p1Color,
    });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = joinCode.trim();
    mpPlayerIdRef.current = result.playerId;
    setMpPlayerId(result.playerId);
    setMpToken(result.token);
    setRoomCode(joinCode.trim());
    setOpponentName(result.opponentName);
    setP1Color(result.opponentColor);
    setP2Color(result.assignedColor);
    setMpConnected(true);
    setMessage(`Connected with ${result.opponentName}. Press New Game to start!`);
    startPolling();
  }

  async function handleLeaveGame() {
    await apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken });
    stopPolling();
    setMpConnected(false);
    setRoomCode("");
    setOpponentName("");
    setMpPlayerId(null);
    setMpToken("");
    setGame(buildGame(level));
    setMessage("Left the game.");
  }

  // ---- CPU move -----------------------------------------------------------

  useEffect(() => {
    if (!isCpuTurn) return;
    const timer = window.setTimeout(() => {
      const move = chooseComputerMove(game.board, level, game.size, game.connect);
      if (move) applyMove(move[0], move[1]);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [isCpuTurn, game, level]);

  // ---- render helpers -----------------------------------------------------

  function getCellClass(r: number, c: number): string {
    const cell = game.board[r][c];
    const isWinning = game.winningCells.some((pos) => pos.r === r && pos.c === c);
    if (cell === 1) return `slot slot-x${isWinning ? " win-cell" : ""}`;
    if (cell === 2) return `slot slot-o${isWinning ? " win-cell" : ""}`;
    return "slot";
  }

  // ---- main render --------------------------------------------------------

  return (
    <div className="game-shell">
      <ConfettiSplash
        active={game.winner !== 0}
        winnerName={
          mode === "multiplayer"
            ? game.winner === mpPlayerId ? "You" : (opponentName || "Opponent")
            : game.winner === 1 ? p1 : game.winner === 2 ? p2 : undefined
        }
      />
      {!hideTitle && <h1 className="title">Tic-Tac-Toe</h1>}

      <section className="control-panel">
        <div className="row two-col">
          <label>
            Mode
            <select value={mode} onChange={(e) => handleModeChange(e.target.value as Mode)}>
              <option value="cpu">Single Player vs Computer</option>
              <option value="local">Two Players Local (Swap Turns)</option>
              <option value="multiplayer">Multiplayer Online</option>
            </select>
          </label>
          <label>
            Level
            <select value={level} onChange={(e) => { setLevel(e.target.value as Level); setGame(buildGame(e.target.value as Level)); }}>
              <option value="easy">Easy ({LEVEL_CONFIGS.easy.label})</option>
              <option value="medium">Medium ({LEVEL_CONFIGS.medium.label})</option>
              <option value="hard">Hard ({LEVEL_CONFIGS.hard.label})</option>
            </select>
          </label>
        </div>

        <div className="row two-col">
          <label>
            {mode === "multiplayer" ? "Your Name (X)" : "Player 1 (X)"}
            <input value={p1Name} onChange={(e) => setP1Name(e.target.value)} maxLength={20} />
          </label>
          {mode !== "multiplayer" && (
            <label>
              {mode === "cpu" ? "Computer (O)" : "Player 2 (O)"}
              <input value={mode === "cpu" ? "Computer" : p2Name} onChange={(e) => setP2Name(e.target.value)} maxLength={20} disabled={mode === "cpu"} />
            </label>
          )}
        </div>

        <div className="row two-col">
          <label>
            {mode === "multiplayer" ? "Your Color" : "Player 1 Color"}
            <select value={p1Color} onChange={(e) => setP1Color(e.target.value as PlayerColor)}>
              {COLOR_OPTIONS.map((c) => (<option key={c} value={c} disabled={mode !== "multiplayer" && c === p2Color}>{toLabel(c)}</option>))}
            </select>
          </label>
          {mode !== "multiplayer" && (
            <label>
              {mode === "cpu" ? "Computer Color" : "Player 2 Color"}
              <select value={p2Color} onChange={(e) => setP2Color(e.target.value as PlayerColor)}>
                {COLOR_OPTIONS.map((c) => (<option key={c} value={c} disabled={c === p1Color}>{toLabel(c)}</option>))}
              </select>
            </label>
          )}
          {mode === "multiplayer" && !mpConnected && !roomCode && (
            <label>
              Room Code
              <input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="e.g. apple-blue-rain" maxLength={40} />
            </label>
          )}
        </div>

        {mode === "multiplayer" && !mpConnected && !roomCode && (
          <div className="mp-actions" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button className="mp-btn" onClick={handleCreateRoom}>Create Room</button>
            <button className="mp-btn mp-btn-secondary" onClick={handleJoinRoom}>Join Room</button>
          </div>
        )}

        {mode === "multiplayer" && roomCode && !mpConnected && (
          <div style={{ textAlign: 'center' }}>
            <p className="mp-label">Share this code with a friend:</p>
            <p className="mp-code">{roomCode}</p>
            <p className="mp-waiting">Waiting for opponent to join...</p>
            <button className="mp-cancel" onClick={handleLeaveGame}>Cancel</button>
          </div>
        )}

        {mode === "multiplayer" && mpConnected && (
          <div className="mp-connected-bar">
            <span className="mp-connected-info">Connected with <strong>{opponentName}</strong></span>
          </div>
        )}

        <div className="actions">
          {mode !== "multiplayer" && <button onClick={startNewGame}>New Game</button>}
          {mode === "multiplayer" && mpConnected && <button onClick={startNewGame}>New Game</button>}
          {mode === "multiplayer" && mpConnected && <button className="mp-leave" onClick={handleLeaveGame}>Leave Game</button>}
        </div>

        {mpError && <p className="subtle mp-error">{mpError}</p>}
        <p className="subtle">{message}</p>
      </section>

      {(mode !== "multiplayer" || mpConnected) && (
        <section className="board-panel">
          <p className="status">{status}</p>
          <div className="toy-frame ttt-grid" style={{ gridTemplateColumns: `repeat(${game.size}, 1fr)`, width: `min(${game.size * 90}px, 100%)` }}>
            {game.board.map((row, r) =>
              row.map((_, c) => (
                <button
                  key={`${r}-${c}`}
                  onClick={() => handleClick(r, c)}
                  className={getCellClass(r, c)}
                  title={`Row ${r + 1}, Col ${c + 1}`}
                  disabled={
                    mode === "multiplayer"
                      ? game.winner !== 0 || game.isDraw || !yourTurn || !mpConnected
                      : game.winner !== 0 || game.isDraw || isCpuTurn
                  }
                />
              ))
            )}
          </div>
          <div className="how-to-play">
            <p className="how-title">How to Play</p>
            <p>1. Pick a mode and level, then press New Game.</p>
            <p>2. Click any empty cell to place your mark.</p>
            <p>3. Get {game.connect} in a row — horizontally, vertically, or diagonally — to win.</p>
          </div>
        </section>
      )}
    </div>
  );
}

function chooseComputerMove(
  board: (0 | 1 | 2)[][],
  level: Level,
  size: number,
  connect: number
): [number, number] | null {
  const empty: Array<[number, number]> = [];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (board[r][c] === 0) empty.push([r, c]);
    }
  }
  if (empty.length === 0) return null;

  if (level === "easy") return empty[Math.floor(Math.random() * empty.length)];

  for (const [r, c] of empty) {
    const copy = board.map((row) => [...row]);
    copy[r][c] = 2;
    if (checkWinner(copy, connect).winner === 2) return [r, c];
  }
  for (const [r, c] of empty) {
    const copy = board.map((row) => [...row]);
    copy[r][c] = 1;
    if (checkWinner(copy, connect).winner === 1) return [r, c];
  }

  if (level === "hard") {
    const mid = Math.floor(size / 2);
    const center = empty.find(([r, c]) => r === mid && c === mid);
    if (center) return center;
    const corners = empty.filter(([r, c]) => (r === 0 || r === size - 1) && (c === 0 || c === size - 1));
    if (corners.length > 0) return corners[Math.floor(Math.random() * corners.length)];
  }

  return empty[Math.floor(Math.random() * empty.length)];
}